import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../../../firebase';
import './marketing.css';

const MARKETING_STORAGE_PATH = 'marketing_photos';
const MAX_IMAGES = 10;
const ACCEPT_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/webp';

const SMS_PRICE = 0.0083;
const MMS_PRICE = 0.02;

/** Same rules as Cloud Function `parseMarketingLogDiscountCode`. */
const MARKETING_LOG_CODE_PATTERN = /^[A-Za-z0-9_-]{2,40}$/;

function normalizeMarketingLogDiscountCodeInput(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return null;
  if (!MARKETING_LOG_CODE_PATTERN.test(t)) return null;
  return t.toUpperCase();
}

function marketingLogDiscountCodesUpperFromLog(marketingSmsSentLog) {
  if (!Array.isArray(marketingSmsSentLog)) return [];
  return [
    ...new Set(
      marketingSmsSentLog
        .map((e) => String(e?.discountCode || '').trim().toUpperCase())
        .filter(Boolean)
    ),
  ];
}

function calculateSMSCost({
  message,
  recipients,
  smsPrice = SMS_PRICE,
  mmsPrice = MMS_PRICE,
  type = 'sms'
}) {
  if (recipients === 0) {
    return type === 'mms' ? { totalCost: 0 } : { segments: 0, totalCost: 0 };
  }
  if (type === 'mms') {
    return {
      totalCost: Number((recipients * mmsPrice).toFixed(4))
    };
  }
  const isUnicode = [...message].some((ch) => ch.codePointAt(0) > 127);
  const segmentLength = isUnicode ? 70 : 160;
  const multiSegmentLength = isUnicode ? 67 : 153;
  const length = message.length;
  let segments = 1;
  if (length > segmentLength) {
    segments = Math.ceil(length / multiSegmentLength);
  }
  const totalCost = segments * recipients * smsPrice;
  return {
    segments,
    totalCost: Number(totalCost.toFixed(4))
  };
}

function Marketing() {
  const [sendTo, setSendTo] = useState('all'); // 'all' | 'all_without_marketing_log_code' | 'customer'
  const [marketingLogDiscountCode, setMarketingLogDiscountCode] = useState('');
  const [optedInCustomers, setOptedInCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(() => new Set()); // customer doc ids
  const [customerSearch, setCustomerSearch] = useState('');
  const [messageText, setMessageText] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]); // uploaded image URLs for MMS
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { sent, failed, errors }
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [shopifyDiscounts, setShopifyDiscounts] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(true);
  const [copiedDiscountId, setCopiedDiscountId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchOptedInCustomers = async () => {
      try {
        const customersRef = collection(db, 'Customers');
        const q = query(customersRef, where('smsSubscribed', '==', true));
        const snapshot = await getDocs(q);
        const list = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          list.push({
            id: doc.id,
            name: data.name || 'No name',
            phone: data.phone || doc.id,
            marketingLogDiscountCodes: marketingLogDiscountCodesUpperFromLog(
              data.marketingSmsSentLog
            ),
          });
        });
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setOptedInCustomers(list);
        if (!list.length) setSelectedCustomerIds(new Set());
      } catch (err) {
        console.error('Error fetching customers:', err);
        setOptedInCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchOptedInCustomers();
  }, []);

  useEffect(() => {
    const fetchShopifyDiscounts = async () => {
      try {
        const ref = collection(db, 'ShopifyDiscounts');
        const snapshot = await getDocs(ref);
        const list = [];
        snapshot.forEach((docSnap) => {
          const d = docSnap.data();
          if (d.action === 'deleted') return;
          list.push({
            id: docSnap.id,
            action: d.action,
            payload: d.payload || {},
            updatedAt: d.updatedAt?.toDate?.()?.toISOString?.() || null
          });
        });
        list.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
        setShopifyDiscounts(list);
      } catch (err) {
        console.error('Error fetching Shopify discounts:', err);
        setShopifyDiscounts([]);
      } finally {
        setLoadingDiscounts(false);
      }
    };
    fetchShopifyDiscounts();
  }, []);

  const getDiscountDisplay = (doc) => {
    const p = doc.payload || {};
    const code = p.code ?? p.discount?.code ?? p.discountNode?.code ?? '';
    const title = p.title ?? p.discount?.title ?? p.discountNode?.title ?? '';
    const isGid = (s) => !s || typeof s !== 'string' || s.startsWith('gid') || s.includes('__');
    const name = title || (isGid(code) ? '' : code) || 'Discount';
    const copyValue = (code && !isGid(code)) ? code : name;
    return { name, copyValue };
  };

  const handleCopyDiscount = async (doc) => {
    const { copyValue } = getDiscountDisplay(doc);
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopiedDiscountId(doc.id);
      setTimeout(() => setCopiedDiscountId(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const uploadImage = async (file) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${MARKETING_STORAGE_PATH}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleImageSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadError(null);
    const toAdd = Math.min(files.length, MAX_IMAGES - mediaUrls.length);
    if (toAdd <= 0) {
      setUploadError(`Maximum ${MAX_IMAGES} images. Remove some to add more.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const urls = [];
      for (let i = 0; i < toAdd; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) continue;
        const url = await uploadImage(file);
        urls.push(url);
      }
      setMediaUrls((prev) => [...prev, ...urls].slice(0, MAX_IMAGES));
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (url) => {
    setMediaUrls((prev) => prev.filter((u) => u !== url));
    setUploadError(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = messageText.trim();
    if (!trimmed) {
      setResult({ sent: 0, failed: 1, errors: ['Please enter a message.'] });
      return;
    }
    if (sendTo === 'customer' && selectedCustomerIds.size === 0) {
      setResult({ sent: 0, failed: 1, errors: ['Please select at least one customer.'] });
      return;
    }
    const logCodeNormalized = normalizeMarketingLogDiscountCodeInput(marketingLogDiscountCode);
    if (sendTo === 'all_without_marketing_log_code') {
      if (!logCodeNormalized) {
        setResult({
          sent: 0,
          failed: 1,
          errors: [
            'Enter a valid log discount code: 2–40 characters using letters, numbers, underscore, or hyphen.',
          ],
        });
        return;
      }
      if (!optedInCustomers.some((c) => !c.marketingLogDiscountCodes.includes(logCodeNormalized))) {
        setResult({
          sent: 0,
          failed: 1,
          errors: [
            `No opted-in customers left — everyone already has “${logCodeNormalized}” on a marketing SMS log row.`,
          ],
        });
        return;
      }
    }
    setSending(true);
    setResult(null);
    try {
      const sendMarketingSms = httpsCallable(functions, 'sendMarketingSms', {
        timeout: 3_600_000,
      });
      const { data } = await sendMarketingSms({
        sendTo,
        customerPhones: sendTo === 'customer' ? Array.from(selectedCustomerIds) : undefined,
        message: trimmed,
        mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
        ...(sendTo === 'all_without_marketing_log_code' && logCodeNormalized
          ? { marketingLogDiscountCode: logCodeNormalized }
          : {}),
      });
      setResult(data);
    } catch (err) {
      setResult({
        sent: 0,
        failed: 1,
        errors: [err.message || String(err)]
      });
    } finally {
      setSending(false);
    }
  };

  const fullMessage = `J&Z Couture: ${messageText} Reply STOP to opt out.`;
  const normalizedLogDiscountCode = normalizeMarketingLogDiscountCodeInput(marketingLogDiscountCode);
  const notYetWithLogCodeCount = normalizedLogDiscountCode
    ? optedInCustomers.filter((c) => !c.marketingLogDiscountCodes.includes(normalizedLogDiscountCode))
      .length
    : 0;
  const recipientCount =
    sendTo === 'all'
      ? optedInCustomers.length
      : sendTo === 'all_without_marketing_log_code'
        ? notYetWithLogCodeCount
        : selectedCustomerIds.size;
  const isMMS = mediaUrls.length > 0;
  const costResult = calculateSMSCost({
    message: fullMessage,
    recipients: recipientCount,
    type: isMMS ? 'mms' : 'sms'
  });
  const smsSegmentCount = costResult.segments ?? (isMMS ? 0 : 1);

  const customerSearchLower = customerSearch.trim().toLowerCase();
  const filteredCustomers =
    !customerSearchLower
      ? optedInCustomers
      : optedInCustomers.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(customerSearchLower) ||
          (c.phone || '').toLowerCase().includes(customerSearchLower) ||
          (c.id || '').toLowerCase().includes(customerSearchLower)
      );

  const excludeSendBlocked =
    sendTo === 'all_without_marketing_log_code' &&
    (!normalizedLogDiscountCode || notYetWithLogCodeCount === 0);
  const customerSendBlocked = sendTo === 'customer' && selectedCustomerIds.size === 0;
  const submitDisabled = sending || excludeSendBlocked || customerSendBlocked;
  const logCodeForButton =
    normalizedLogDiscountCode ||
    (marketingLogDiscountCode.trim() ? marketingLogDiscountCode.trim().toUpperCase() : null);
  const submitButtonLabel = sending
    ? 'Sending...'
    : sendTo === 'all_without_marketing_log_code'
      ? !marketingLogDiscountCode.trim()
        ? `Enter log code — then send ${isMMS ? 'MMS' : 'SMS'}`
        : !normalizedLogDiscountCode
          ? `Fix code format — 2–40 chars (A–Z, 0–9, _ -)`
          : notYetWithLogCodeCount === 0
            ? `No one left without “${normalizedLogDiscountCode}” in log`
            : `Send ${isMMS ? 'MMS' : 'SMS'} — ${notYetWithLogCodeCount} without “${normalizedLogDiscountCode}” in log`
      : `Send marketing ${isMMS ? 'MMS' : 'SMS'}${recipientCount > 0 ? ` (${recipientCount})` : ''}`;

  return (
    <div className="marketing-container">
      <div className="marketing-layout">
        <div className="marketing-section">
          <div className="marketing-header">
          <h1>SMS Marketing</h1>
          <p>Send SMS to opted-in customers. Add custom text, a promo code, or a link.</p>
        </div>
        <div className="marketing-card">
          <h2 className="marketing-card-title">Send marketing notification</h2>
          <form className="marketing-form" onSubmit={handleSend}>
            <div className="marketing-field">
              <label className="marketing-label">Send to</label>
              <div className="marketing-radio-group">
                <label className="marketing-radio">
                  <input
                    type="radio"
                    name="sendTo"
                    checked={sendTo === 'all'}
                    onChange={() => setSendTo('all')}
                  />
                  <span>All opted-in customers</span>
                  {sendTo === 'all' && (
                    <span className="marketing-recipient-count">({optedInCustomers.length} customer{optedInCustomers.length !== 1 ? 's' : ''})</span>
                  )}
                </label>
                <label className="marketing-radio">
                  <input
                    type="radio"
                    name="sendTo"
                    checked={sendTo === 'all_without_marketing_log_code'}
                    onChange={() => setSendTo('all_without_marketing_log_code')}
                  />
                  <span>Opted-in who do not have this code in their marketing log</span>
                  {sendTo === 'all_without_marketing_log_code' && normalizedLogDiscountCode && (
                    <span className="marketing-recipient-count">
                      ({notYetWithLogCodeCount} customer{notYetWithLogCodeCount !== 1 ? 's' : ''})
                    </span>
                  )}
                </label>
                <label className="marketing-radio">
                  <input
                    type="radio"
                    name="sendTo"
                    checked={sendTo === 'customer'}
                    onChange={() => setSendTo('customer')}
                  />
                  <span>Specific customers</span>
                  {sendTo === 'customer' && selectedCustomerIds.size > 0 && (
                    <span className="marketing-recipient-count">({selectedCustomerIds.size} customer{selectedCustomerIds.size !== 1 ? 's' : ''})</span>
                  )}
                </label>
              </div>
              <p className="marketing-recipient-summary">
                {recipientCount === 0
                  ? sendTo === 'customer'
                    ? 'Select one or more customers below.'
                    : sendTo === 'all_without_marketing_log_code'
                      ? !normalizedLogDiscountCode && marketingLogDiscountCode.trim()
                        ? 'Enter a valid log code (2–40 letters, numbers, underscore, or hyphen).'
                        : !normalizedLogDiscountCode
                          ? 'Enter a log discount code to see how many customers qualify.'
                          : 'No opted-in customers without that code on their marketing SMS log.'
                      : 'No opted-in customers.'
                  : `${recipientCount} customer${recipientCount !== 1 ? 's' : ''} will receive this message.`}
              </p>
              {sendTo === 'all_without_marketing_log_code' && (
                <div className="marketing-field marketing-log-code-field">
                  <label htmlFor="marketing-log-discount-code" className="marketing-label">
                    Log discount code
                  </label>
                  <input
                    id="marketing-log-discount-code"
                    type="text"
                    className="marketing-search-input marketing-log-code-input"
                    value={marketingLogDiscountCode}
                    onChange={(e) => setMarketingLogDiscountCode(e.target.value)}
                    placeholder="e.g. JZVIP30"
                    autoComplete="off"
                    maxLength={40}
                    aria-describedby="marketing-log-code-hint"
                  />
                  <p id="marketing-log-code-hint" className="marketing-hint marketing-recipient-hint">
                    Skips opted-in customers who already have this exact code (case-insensitive) on any{' '}
                    <code>marketingSmsSentLog</code> row. Successful sends add a log row with this code.
                  </p>
                </div>
              )}
            </div>

            {sendTo === 'customer' && (
              <div className="marketing-field">
                <label className="marketing-label">Customers</label>
                <div className="marketing-customer-actions">
                  <button
                    type="button"
                    className="marketing-link-btn"
                    onClick={() => setSelectedCustomerIds(new Set(filteredCustomers.map((c) => c.id)))}
                    disabled={loadingCustomers || filteredCustomers.length === 0}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="marketing-link-btn"
                    onClick={() => setSelectedCustomerIds(new Set())}
                    disabled={loadingCustomers || selectedCustomerIds.size === 0}
                  >
                    Clear
                  </button>
                </div>
                <input
                  type="search"
                  className="marketing-search-input"
                  placeholder="Search by name or phone..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  disabled={loadingCustomers}
                  aria-label="Search customers by name or phone"
                />
                {!loadingCustomers && optedInCustomers.length > 0 && customerSearch.trim() && (
                  <p className="marketing-hint marketing-search-hint">
                    Showing {filteredCustomers.length} of {optedInCustomers.length} customer{optedInCustomers.length !== 1 ? 's' : ''}
                  </p>
                )}
                <div className="marketing-customer-list" role="group" aria-label="Select customers">
                  {loadingCustomers && <p className="marketing-hint">Loading opted-in customers...</p>}
                  {!loadingCustomers && optedInCustomers.length === 0 && (
                    <p className="marketing-hint">No opted-in customers found.</p>
                  )}
                  {!loadingCustomers && optedInCustomers.length > 0 && filteredCustomers.length === 0 && (
                    <p className="marketing-hint">No customers match your search.</p>
                  )}
                  {!loadingCustomers && filteredCustomers.length > 0 && (
                    <ul className="marketing-checkbox-list">
                      {filteredCustomers.map((c) => (
                        <li key={c.id}>
                          <label className="marketing-checkbox-label">
                            <input
                              type="checkbox"
                              checked={selectedCustomerIds.has(c.id)}
                              onChange={(e) => {
                                const next = new Set(selectedCustomerIds);
                                if (e.target.checked) next.add(c.id); else next.delete(c.id);
                                setSelectedCustomerIds(next);
                              }}
                            />
                            <span>{c.name} ({c.phone || c.id})</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="marketing-field marketing-field-images">
              <label className="marketing-label">Images (MMS)</label>
              <p className="marketing-hint marketing-images-hint">Add up to {MAX_IMAGES} images to send as MMS. JPEG, PNG, GIF, or WebP.</p>
              <div
                className={`marketing-upload-zone ${uploading ? 'is-uploading' : ''} ${mediaUrls.length >= MAX_IMAGES ? 'is-full' : ''}`}
                onClick={() => !uploading && mediaUrls.length < MAX_IMAGES && fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === 'Enter' && !uploading && mediaUrls.length < MAX_IMAGES && fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                aria-label="Add images for MMS"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_IMAGE_TYPES}
                  multiple
                  onChange={handleImageSelect}
                  disabled={uploading || mediaUrls.length >= MAX_IMAGES}
                  className="marketing-file-input"
                  aria-label="Upload images for MMS"
                />
                <span className="marketing-upload-icon" aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                </span>
                <span className="marketing-upload-text">
                  {uploading ? 'Uploading…' : mediaUrls.length >= MAX_IMAGES ? `${MAX_IMAGES} images added` : 'Click to add images'}
                </span>
                {mediaUrls.length > 0 && mediaUrls.length < MAX_IMAGES && !uploading && (
                  <span className="marketing-upload-count">{mediaUrls.length} of {MAX_IMAGES}</span>
                )}
              </div>
              {uploadError && <p className="marketing-upload-error">{uploadError}</p>}
              {mediaUrls.length > 0 && (
                <ul className="marketing-media-list">
                  {mediaUrls.map((url) => (
                    <li key={url} className="marketing-media-item">
                      <img src={url} alt="" className="marketing-media-thumb" />
                      <button
                        type="button"
                        className="marketing-media-remove"
                        onClick={(e) => { e.stopPropagation(); removeImage(url); }}
                        aria-label="Remove image"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="marketing-field">
              <label htmlFor="marketing-message" className="marketing-label">Message</label>
              <textarea
                id="marketing-message"
                className="marketing-textarea"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="e.g. Summer sale! Use code SAVE20 at checkout. Or: Book your fitting at https://..."
                rows={5}
                maxLength={1000}
              />
              <p className="marketing-hint">
                Your text will be sent as: &quot;J&Z Couture: [your message] Reply STOP to opt out.&quot;
                <span className="marketing-char-count">
                  {' '}({messageText.length} char{messageText.length !== 1 ? 's' : ''}
                  {!isMMS && messageText.length > 0 && `, ${smsSegmentCount} segment${smsSegmentCount !== 1 ? 's' : ''}`})
                </span>
              </p>
              {recipientCount > 0 && (
                <p className="marketing-cost-summary">
                  {isMMS ? (
                    <>MMS: {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} × ${MMS_PRICE.toFixed(2)} = <strong>${costResult.totalCost.toFixed(2)}</strong></>
                  ) : (
                    <>SMS: {costResult.segments} segment{costResult.segments !== 1 ? 's' : ''} × {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} × ${SMS_PRICE.toFixed(4)} = <strong>${costResult.totalCost.toFixed(2)}</strong></>
                  )}
                </p>
              )}
            </div>

            {result && (
              <div className={`marketing-result ${result.failed > 0 ? 'has-errors' : 'success'}`}>
                <p>
                  Sent: {result.sent}, Failed: {result.failed}
                  {typeof result.skipped === 'number' && result.skipped > 0 && (
                    <>
                      , Skipped (already had &quot;{result.marketingLogDiscountCode || logCodeForButton}&quot; in log):{' '}
                      {result.skipped}
                    </>
                  )}
                </p>
                {result.errors && result.errors.length > 0 && (
                  <ul className="marketing-result-errors">
                    {result.errors.slice(0, 15).map((err, i) => <li key={i}>{err}</li>)}
                    {result.errors.length > 15 && <li>… and {result.errors.length - 15} more</li>}
                  </ul>
                )}
              </div>
            )}

            <div className="marketing-actions">
              {recipientCount > 0 && (
                <p className="marketing-cost-inline">
                  Est. cost: <strong>${costResult.totalCost.toFixed(2)}</strong>
                  {isMMS && ` (MMS)`}
                  {!isMMS && costResult.segments > 0 && ` (${costResult.segments} segment${costResult.segments !== 1 ? 's' : ''})`}
                </p>
              )}
              <button type="submit" className="marketing-submit-btn" disabled={submitDisabled}>
                {submitButtonLabel}
              </button>
            </div>
          </form>
        </div>
        </div>
        <aside className="shopify-discounts-section">
          <h2 className="shopify-discounts-title">Discount codes</h2>
          {loadingDiscounts ? (
            <p className="shopify-discounts-loading">Loading…</p>
          ) : shopifyDiscounts.length === 0 ? (
            <p className="shopify-discounts-empty">No codes synced from Shopify yet.</p>
          ) : (
            <ul className="shopify-discounts-list">
              {shopifyDiscounts.map((doc) => {
                const { name } = getDiscountDisplay(doc);
                const isCopied = copiedDiscountId === doc.id;
                return (
                  <li
                    key={doc.id}
                    role="button"
                    tabIndex={0}
                    className="shopify-discount-card"
                    onClick={() => handleCopyDiscount(doc)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCopyDiscount(doc)}
                    title="Click to copy"
                  >
                    <span className="shopify-discount-name">{name}</span>
                    {isCopied && <span className="shopify-discount-copied">Copied!</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

export default Marketing;
